import { Link, useLocation } from 'react-router-dom';
import { AiOutlineProduct } from "react-icons/ai";
import { RiShoppingCartLine } from "react-icons/ri";
/*import { RiFileList3Line } from "react-icons/ri";*/
/*import { IoReceiptOutline } from "react-icons/io5";*/
import { TbReceipt } from "react-icons/tb";
import { RiHome9Line } from "react-icons/ri";
import { IoPersonOutline } from "react-icons/io5";
import { useState, useEffect, useRef } from 'react';
import { useCartStore } from '@/store/cartStore';
import { cn } from '@/lib/utils';

export function MobileBottomNav() {
  const location = useLocation();
  const cartItems = useCartStore(state => state.items);
  const cartItemCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const prevCountRef = useRef(cartItemCount);

  useEffect(() => {
    if (cartItemCount > prevCountRef.current) {
      if (import.meta.env.DEV) console.log('Cart feedback triggered');
      
      const timerStart = setTimeout(() => {
        setIsAnimating(true);
        setAnimationKey(prev => prev + 1); // Force restart animation
      }, 0);
      
      const timerEnd = setTimeout(() => setIsAnimating(false), 2000);
      prevCountRef.current = cartItemCount;
      
      return () => {
        clearTimeout(timerStart);
        clearTimeout(timerEnd);
      };
    } else if (cartItemCount < prevCountRef.current) {
      prevCountRef.current = cartItemCount;
    }
  }, [cartItemCount]);

  const mobileNavItems = [
    { href: '/', label: 'Home', icon: RiHome9Line, isLucide: false, position: 'left' as const },
    { href: '/products', label: 'Products', icon: AiOutlineProduct, isLucide: false, position: 'left' as const },
    { href: '/cart', label: 'Cart', icon: RiShoppingCartLine, isLucide: false, position: 'center' as const },
    { href: '/orders', label: 'Orders', icon: TbReceipt, isLucide: false, position: 'right' as const },
    { href: '/account', label: 'Account', icon: IoPersonOutline, isLucide: false, position: 'right' as const },
  ];

  const centerItem = mobileNavItems.find(item => item.position === 'center');

  // Hide the global bottom nav on product detail pages to maximize screen space for the product action bar
  const isProductDetail = location.pathname.match(/^\/products?\//);
  if (isProductDetail) return null;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 lg:hidden" style={{ pointerEvents: 'none' }}>
      <div className="relative mx-auto max-w-full" style={{ pointerEvents: 'auto' }}>
        
        {/* Floating center cart button */}
        {centerItem && (
          <Link
            to={centerItem.href}
            className="absolute left-1/2 -translate-x-1/2 -top-5 z-20 flex items-center justify-center"
          >
            {/* 
              The button has a drop shadow that casts onto the curve below it. 
              The gap between this button and the SVG curve below forms the "shadow space".
            */}
            <div 
              key={animationKey}
              className={cn(
              "flex h-[56px] w-[56px] items-center justify-center rounded-full active:scale-95 transition-transform duration-200",
              location.pathname === centerItem.href ? "chocolate-beveled text-white shadow-md" : "bg-white shadow-[0_4px_12px_rgba(0,0,0,0.15)]",
              isAnimating ? "cart-feedback-active duration-200" : "duration-0"
            )}>
              <centerItem.icon
                className={`h-8 w-8 transition-colors duration-200 ${
                  location.pathname === centerItem.href ? 'text-white' : 'text-black'
                }`}
                {...(centerItem.isLucide ? { strokeWidth: 1.8 } : {})}
              />
            </div>
          </Link>
        )}
        
        {/* 
          SVG curved background
          The path draws a straight line, then a tight, deep "U" shape (A parameter) 
          that perfectly hugs the circular button above it, leaving a small, even gap.
        */}
        <svg
          className="absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 60"
          preserveAspectRatio="xMidYMin slice"
          style={{ height: '60px', filter: 'drop-shadow(0 -4px 16px rgba(0,0,0,0.08))' }}
        >
          <path
            d="M0,0 L669,0 A18,18 0 0,1 686.6,14.5 A34,34 0 0,0 753.4,14.5 A18,18 0 0,1 771,0 L1440,0 L1440,60 L0,60 Z"
            fill="#ffffff"
          />
        </svg>

        {/* Nav items container */}
        <div className="relative z-10 flex items-end justify-around px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]" style={{ height: '60px' }}>
          {mobileNavItems.map((item) => {
            const active = item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

            // Center item is rendered as the floating button above
            if (item.position === 'center') {
              return <div key={item.href} className="w-16" />;
            }

            return (
              <Link
                key={item.href}
                to={item.href}
                className="flex flex-col items-center gap-0 py-0 min-w-[56px] mb-0 group"
              >
                <div className={cn(
                  "flex items-center justify-center rounded-full transition-all duration-300",
                  active ? "w-8 h-8 chocolate-beveled text-white" : "w-8 h-8 text-black bg-transparent"
                )}>
                  <item.icon
                    className={cn("transition-colors duration-200 h-[22px] w-[22px]", active ? "text-white" : "")}
                    {...(item.isLucide ? { strokeWidth: 1.8 } : {})}
                  />
                </div>
                <span
                  className={cn(
                    "text-[10px] font-medium transition-all duration-300",
                    active ? "text-primary font-bold opacity-100" : "text-black"
                  )}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
